import { motion } from "framer-motion";
import {
  Globe,
  Star,
  MessageSquare,
  Inbox,
  Search,
  CalendarClock,
  Bot,
  ArrowRight,
  Play,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { soluciones } from "@/lib/soluciones";

const iconMap = {
  globe: Globe,
  star: Star,
  message: MessageSquare,
  inbox: Inbox,
  search: Search,
  clock: CalendarClock,
  bot: Bot,
};

export const ServiciosSection = () => {
  const navigate = useNavigate();
  const [activeVideo, setActiveVideo] = useState<{
    url: string;
    title: string;
    thumbnail?: string;
  } | null>(null);

  const videoSoluciones = soluciones.filter((s) => s.videoUrl);
  const currentIndex = activeVideo
    ? videoSoluciones.findIndex((s) => s.videoUrl === activeVideo.url)
    : -1;

  const goPrev = () => {
    if (currentIndex > 0) {
      const prev = videoSoluciones[currentIndex - 1];
      setActiveVideo({
        url: prev.videoUrl!,
        title: prev.title,
        thumbnail: prev.thumbnailUrl,
      });
    }
  };

  const goNext = () => {
    if (currentIndex >= 0 && currentIndex < videoSoluciones.length - 1) {
      const next = videoSoluciones[currentIndex + 1];
      setActiveVideo({
        url: next.videoUrl!,
        title: next.title,
        thumbnail: next.thumbnailUrl,
      });
    }
  };

  return (
    <section
      id="servicios"
      className="bg-[#08080A] py-24 lg:py-[140px] relative scroll-mt-16"
    >
      <div className="container mx-auto px-6 max-w-[1200px]">
        <div className="max-w-3xl mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-[#71717A] font-mono text-[13px] tracking-[0.08em] uppercase mb-6">
              ● TODO CONECTADO
            </p>
            <h2 className="text-[32px] md:text-[44px] lg:text-[56px] font-medium text-[#FAFAFA] tracking-[-0.03em] leading-[1.05] mb-6">
              Las herramientas trabajan juntas.
            </h2>
            <p className="text-[#A1A1AA] text-[16px] md:text-[17px] leading-[1.6] max-w-[600px]">
              Un sistema. Lo armamos nosotros en 7 a 10 días y vos no tenés que
              aprender marketing, automatizaciones ni tecnología. Cada pieza
              responde a &ldquo;¿para qué me sirve?&rdquo; antes que a qué
              tecnología usa.
            </p>
          </motion.div>
        </div>

        <div className="space-y-4">
          {soluciones.map((block, index) => {
            const Icon = iconMap[block.icon];
            return (
              <motion.div
                key={block.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.06,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="bg-[#0F0F11] border border-white/5 rounded-[16px] p-8 lg:p-10 hover:border-white/10 transition-all duration-300 group cursor-pointer"
                style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)" }}
                onClick={() => navigate(`/que-hacemos/${block.slug}`)}
              >
                <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 lg:gap-8">
                  <div className="flex lg:flex-col items-center gap-4">
                    <div className="w-12 h-12 bg-white/5 border border-white/10 rounded-[12px] flex items-center justify-center shrink-0">
                      <Icon className="w-6 h-6 text-[#7C3AED]" />
                    </div>
                    <span className="text-[24px] font-mono font-medium text-transparent bg-clip-text bg-gradient-to-r from-[#7C3AED] to-[#FB7185]">
                      {block.number}
                    </span>
                  </div>

                  <div>
                    <h3 className="text-[#FAFAFA] text-[22px] lg:text-[24px] font-medium tracking-[-0.03em] mb-3">
                      {block.title}
                    </h3>
                    <p className="text-[#A1A1AA] text-[15px] leading-[1.6] mb-6 italic">
                      {block.tagline}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                      {block.bullets.map((bullet, bidx) => (
                        <div key={bidx}>
                          <p className="text-[#FAFAFA] text-[14px] font-medium mb-1">
                            {bullet.label}
                          </p>
                          <p className="text-[#A1A1AA] text-[14px] leading-[1.5]">
                            {bullet.text}
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 mt-6">
                      <div
                        className="inline-flex items-center gap-2 text-[#A78BFA] font-mono text-[13px] tracking-[0.08em] uppercase hover:text-[#C4B5FD] transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (block.videoUrl) {
                            setActiveVideo({
                              url: block.videoUrl,
                              title: block.title,
                              thumbnail: block.thumbnailUrl,
                            });
                          } else {
                            navigate(`/que-hacemos/${block.slug}`);
                          }
                        }}
                      >
                        Ver cómo funciona
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Video Modal */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setActiveVideo(null)}
        >
          <div
            className="relative w-full max-w-3xl bg-[#0F0F11] border border-white/10 rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <h3 className="text-[#FAFAFA] text-[16px] font-medium tracking-[-0.02em]">
                {activeVideo.title}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  className="w-9 h-9 flex items-center justify-center rounded-lg text-[#A1A1AA] hover:text-white hover:bg-white/5 transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#A1A1AA]"
                  onClick={goPrev}
                  disabled={currentIndex <= 0}
                  aria-label="Anterior"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  className="w-9 h-9 flex items-center justify-center rounded-lg text-[#A1A1AA] hover:text-white hover:bg-white/5 transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#A1A1AA]"
                  onClick={goNext}
                  disabled={currentIndex >= videoSoluciones.length - 1}
                  aria-label="Siguiente"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <button
                  className="w-9 h-9 flex items-center justify-center rounded-lg text-[#A1A1AA] hover:text-white hover:bg-white/5 transition-colors ml-1"
                  onClick={() => setActiveVideo(null)}
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="aspect-video bg-black">
              <video
                key={activeVideo.url}
                src={activeVideo.url}
                poster={activeVideo.thumbnail}
                controls
                autoPlay
                playsInline
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
